import { useI18n } from "@/lib/i18n";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { VenusParticles } from "@/components/venus-particles";
import { projects } from "@/data/projects";
import { projectThumbs } from "@/data/thumbs";
import { pageUrl } from "@/lib/lang-url";

export const Route = createFileRoute("/works")({
  head: ({ match }) => ({
    meta: [
      { property: "og:url", content: pageUrl("/works", match.context.getLang()) },
      { title: "Works — Index of Projects | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Works developed since 2010 around the decolonisation of mental and public health through social hacking, counter-archive and the rewriting of narratives through the image.",
      },
      { property: "og:title", content: "Works — Index of Projects | Luiza Jesus do Prado" },
      {
        property: "og:description",
        content: "Selected works — installation, performance, photography, sound and research.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Works,
});

/** Same selection shown on the home rail. */
const selectedSlugs = [
  "camara-obscura",
  "ecologies-of-perception",
  "the-weight-of-feeling",
  "selfie",
  "what-is-madness",
  "lacuna",
  "garden-of-dreams-and-nightmares-situated-between-the-loom",
  "butoh-caipira",
  "appropriation",
  "entities",
  "symbiogenesis",
  "colonized-sexuality",
  "diary-of-memories-that-i-dont-have",
  "topographies-of-violence",
  "discourses-of-necropolitics",
  "apophenia-and-pareidolia",
  "body-sound",
  "reprocessing",
  "memoria",
];

const titles: Record<string, string> = {
  "ecologies-of-perception": "Ecologies of Perception",
};

const otherWorks: { label: string; to?: string; href?: string }[] = [
  { label: "Photo", to: "/photo" },
  { label: "Performance", href: "https://www.luizajesusdoprado.com/performance" },
  { label: "Sound", href: "https://www.luizajesusdoprado.com/sound" },
  { label: "Write", href: "https://www.luizajesusdoprado.com/write" },
  { label: "Science", href: "https://www.luizajesusdoprado.com/science" },
];

function Works() {
  const { tx } = useI18n();
  const items = selectedSlugs
    .map((slug) => projects.find((p) => p.slug === slug))
    .filter((p): p is (typeof projects)[number] => Boolean(p));

  const stageRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  // vertical scroll drives the horizontal travel of the rail
  useEffect(() => {
    const compute = () => {
      const stage = stageRef.current;
      const track = trackRef.current;
      if (!stage || !track) return;
      const distance = Math.max(0, track.scrollWidth - window.innerWidth);
      const start = stage.offsetTop;
      const span = stage.offsetHeight - window.innerHeight;
      const progress = span > 0 ? (window.scrollY - start) / span : 0;
      setOffset(Math.min(1, Math.max(0, progress)) * distance);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [items.length]);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      <main id="main-content" className="pb-0 pt-32 md:pt-40">
        <div className="px-4 md:px-[6vw]">
          {/* intro */}
          <h1 className="mb-8 font-display text-[clamp(1.8rem,4vw,3.2rem)] font-light leading-[1] text-bone">
            {tx({ en: "Works / Projects", pt: "Trabalhos / Projetos", es: "Trabajos / Proyectos" })}
          </h1>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <p className="font-sans text-[11px] font-light leading-[1.5] text-bone/90 md:col-span-6">
              {tx({
                en: "This page gathers works developed or in development since 2010, grounded in my research on the decolonisation of mental health and public health through social hacking, counter-archive and the rewriting of narratives through the image — alongside studies and proposals for social-scientific approaches that use art as a resource.",
                pt: "Esta página reúne trabalhos desenvolvidos ou em desenvolvimento desde 2010, a partir da minha pesquisa sobre a descolonização da saúde mental e da saúde pública por meio do hackeamento social, do contra-arquivo e da reescrita de narrativas pela imagem, ao lado de estudos e propostas de abordagens sociocientíficas que usam a arte como recurso.",
                es: "Esta página reúne trabajos desarrollados o en desarrollo desde 2010, a partir de mi investigación sobre la descolonización de la salud mental y la salud pública mediante el hackeo social, el contraarchivo y la reescritura de narrativas a través de la imagen, junto con estudios y propuestas de enfoques sociocientíficos que usan el arte como recurso.",
              })}
            </p>
          </div>

          {/* other works */}
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12">
            <div className="md:col-span-2">
              <span className="font-sans text-[11px] font-light text-bone/60">{tx({ en: "Other works:", pt: "Outros trabalhos:", es: "Otros trabajos:" })}</span>
            </div>
            <div className="md:col-span-4">
              <ul className="space-y-1">
                {otherWorks.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        className="font-sans text-[11px] font-light text-bone/90 transition-opacity hover:opacity-60"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noreferrer"
                        className="font-sans text-[11px] font-light text-bone/90 transition-opacity hover:opacity-60"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* parallax horizontal rail — vertical scroll pushes the images sideways */}
        <section ref={stageRef} style={{ height: `${items.length * 55 + 100}vh` }} className="relative mt-2 md:mt-4">
          <div
            className="sticky top-0 flex h-screen cursor-grab items-start overflow-hidden pt-8 active:cursor-grabbing md:pt-10"
            onWheel={(e) => {
              if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) window.scrollBy({ top: e.deltaX });
            }}
            onPointerDown={(e) => {
              const startX = e.clientX;
              const startY = window.scrollY;
              const move = (ev: PointerEvent) =>
                window.scrollTo({ top: startY + (startX - ev.clientX) * 1.6 });
              const up = () => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
              };
              window.addEventListener("pointermove", move);
              window.addEventListener("pointerup", up);
            }}
          >
            <div
              ref={trackRef}
              className="flex gap-[6px] px-4 will-change-transform md:px-[6vw]"
              style={{ transform: `translate3d(${-offset}px,0,0)`, transition: "transform 120ms linear" }}
            >
              {items.map((p) => (
                <Link
                  key={p.slug}
                  to="/work/$slug"
                  params={{ slug: p.slug }}
                  className="group relative block shrink-0 overflow-hidden"
                  style={{ width: "min(78vw, 34vw)" }}
                >
                  {p.slug === "lacuna" ? (
                    <VenusParticles className="h-[62vh] w-full bg-black" />
                  ) : (
                    <img
                      src={projectThumbs[p.slug] ?? p.cover}
                      alt={p.title}
                      loading="lazy"
                      className="h-[62vh] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                    />
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-ink px-3 py-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="font-sans text-[11px] font-light leading-tight">
                      {titles[p.slug] ?? p.title}
                    </span>
                    <span className="font-sans text-[11px] leading-none text-bone/80">
                      {"+"}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
